import React, { useEffect } from 'react'
import './styles/main.scss'
import Editor from './components/Editor'
import ContentPreview from './components/ContentPreview'
import { webContentStore } from '../../../../../state/setting/webContent'
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import { useDeleteWebContent, useGetWebContent } from 'api/Settings/WebContent'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { useState } from 'react'
import moment from 'moment'
import Spinner from 'components/Spinner'
import ReorderModal from './Modals/ReorderModal'

const Website: React.FC = () => {
  const { slides, currentSlide, setCurrentSlide, removeSlide } =
    webContentStore()

  const {
    setSlides,
    setHeading,
    setHeadingBefore,
    setHeadingColored,
    setHeadingAfter,
    setDescription,
    setButtonText,
    setButtonLink,
    reorderSlidesModal,
    setReorderSlidesModal,
  } = webContentStore(store => ({
    setSlides: store.setSlides,
    setHeading: store.setHeading,
    setHeadingBefore: store.setHeadingBefore,
    setHeadingColored: store.setHeadingColored,
    setHeadingAfter: store.setHeadingAfter,
    setDescription: store.setDescription,
    setButtonText: store.setButtonText,
    setButtonLink: store.setButtonLink,
    reorderSlidesModal: store.reorderSlidesModal,
    setReorderSlidesModal: store.setReorderSlidesModal,
  }))

  const { data, isLoading, isFetching, refetch } = useGetWebContent()
  useEffect(() => {
    refetch()
  }, [])

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const mappedSlides = data.map((item, idx) => {
        const galleryPlaceholders = Array.from({ length: 9 }, () => '')
        if (Array.isArray(item.galleries)) {
          item.galleries.forEach(g => {
            if (!g) return
            const index = (g.imageIndex || 0) - 1
            if (index >= 0 && index < 9) {
              const src = g.image?.thumb || g.image?.original || ''
              galleryPlaceholders[index + 1] = src || ''
            }
          })
        }
        let galleryIds = []
        if (Array.isArray(item.galleries)) {
          item.galleries.forEach((g, index) => {
            if (!g) return
            galleryIds.push({
              imageIndex: index,
              imageId: g.image?._id || undefined,
            })
          })
        }

        return {
          id: item.id || item._id || idx + 1,
          content: {
            heading: item.content?.heading || '',
            headingBefore: item.content?.headingBefore || '',
            headingColored: item.content?.headingColored || '',
            headingAfter: item.content?.headingAfter || '',
            description: item.content?.description || '',
            buttonText: item.content?.buttonText || '',
            buttonLink: item.content?.buttonLink || '#',
          },
          type: item.type || 'none',
          videoLink: item.videoLink || undefined,
          imageLink: item.imageLink || undefined,
          commonEventId: item.commonEventId || undefined,
          event:
            item.type === 'commonEvent'
              ? {
                  name: item.commonEvent?.name,
                  coverPhoto: item.commonEvent?.coverPhotos[0]?.original || '',
                  date: moment(item.commonEvent.datesOfEvent[0]).format(
                    'YYYY-MM-DD',
                  ),
                  venue:
                    item.commonEvent.venueType === 'virtual'
                      ? 'Online'
                      : item.commonEvent.venueType === 'to-be-determined'
                      ? 'TBD'
                      : `${item.commonEvent.location.apartment}, ${item.commonEvent.location.city} ${item.commonEvent.location.state}`,
                }
              : undefined,
          gallery: galleryPlaceholders,
          galleryIds: galleryIds,
        }
      })
      setSlides(mappedSlides)

      // Preserve previous single-slide editor previews (optional defaults from first)
      if (mappedSlides[0]) {
        setHeading(mappedSlides[0].content.heading || 'Enter text')
        setHeadingBefore(
          mappedSlides[0].content.headingBefore || 'Before title',
        )
        setHeadingColored(
          mappedSlides[0].content.headingColored || 'Colored title',
        )
        setHeadingAfter(mappedSlides[0].content.headingAfter || 'After title')
        setDescription(
          mappedSlides[0].content.description || 'Few words about the external',
        )
        setButtonText(mappedSlides[0].content.buttonText || 'Learn more')
        setButtonLink(mappedSlides[0].content.buttonLink || '#')
      }
    }
  }, [data])

  //dynamic content

  const [clickedId, setClickedId] = useState<string | number | undefined>('')
  const { mutate: deleteWebContentMutation, isLoading: deleteLoading } =
    useDeleteWebContent()

  const handleDeleteSlide = (slideId: string) => {
    if (slideId.includes('local-')) {
      removeSlide(slideId)
      const newSlidesLength = webContentStore.getState().slides.length

      if (newSlidesLength === 0) {
        setCurrentSlide(0)
      } else if (currentSlide >= newSlidesLength) {
        setCurrentSlide(newSlidesLength - 1)
      }
    } else {
      setClickedId(slideId)
      deleteWebContentMutation(slideId as string, {
        onSuccess: () => {
          removeSlide(slideId)
          setClickedId(undefined)
          const newSlidesLength = webContentStore.getState().slides.length

          if (newSlidesLength === 0) {
            setCurrentSlide(0)
          } else if (currentSlide >= newSlidesLength) {
            setCurrentSlide(newSlidesLength - 1)
          }
        },
        onError: () => {
          setClickedId(undefined)
        },
      })
    }
  }

  const handleReorder = () => {
    setReorderSlidesModal(true)
  }

  return (
    <>
      <div className="main-website-container">
        <h3 className="text-24 w-600">Website content</h3>
        <p className="text-16 w-400  text-gray">
          Edit content on the main website page
        </p>
        <div className="section-counter-container">
          <div className="flex gap-20px align-center">
            <h4 className="text-20 w-600">
              Section {currentSlide + 1} of {slides.length}
            </h4>
            <div className="flex gap-20px align-center">
              <ArrowLeft
                size={20}
                color="#2BA7CA"
                className="pointer"
                onClick={() =>
                  setCurrentSlide(
                    currentSlide === 0 ? slides.length - 1 : currentSlide - 1,
                  )
                }
              />
              <ArrowRight
                size={20}
                color="#2BA7CA"
                className="pointer"
                onClick={() =>
                  setCurrentSlide(
                    currentSlide === slides.length - 1 ? 0 : currentSlide + 1,
                  )
                }
              />
            </div>
          </div>
          <div className="btn-container">
            {slides.length > 0 &&
              (clickedId === slides[currentSlide]?.id && deleteLoading ? (
                <ButtonSpinner size={20} color="#2ba7ca" />
              ) : (
                <button
                  className="btn-delete"
                  onClick={() =>
                    handleDeleteSlide(slides[currentSlide].id as string)
                  }
                >
                  Delete Section
                </button>
              ))}
            <button className="bordered-prim-btn" onClick={handleReorder}>
              Reorder
            </button>
            <button
              className="bordered-prim-btn"
              onClick={() => webContentStore.getState().addSlide()}
            >
              Add Section
            </button>
          </div>
        </div>
        <div className="main-content-container">
          {isLoading || isFetching ? (
            <div className="w-full flex justify-center items-center py-40px">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="editor-container">
                <Editor />
              </div>
              <div
                className="preview-container"
                style={{ backgroundColor: '#f6f6f3', borderRadius: '40px' }}
              >
                <ContentPreview />
              </div>
            </>
          )}
        </div>
      </div>

      {reorderSlidesModal && <ReorderModal />}
    </>
  )
}

export default Website
